/**
 * Feb 6, 2026 — Combined Delinquent Payoff visibility rules.
 *
 * The "Delinquent Payoff Breakdown" toggle is operationally meaningful
 * only when there's a realistic possibility of multiple delinquent
 * obligations being covered by a single collector payoff.
 *
 *   • Annual collection with ≤1 delinquent record → HIDE the section
 *     (the per-record Payoff Amount + Good Through fields already
 *     handle the single-obligation case cleanly).
 *   • Annual with ≥2 delinquent records → SHOW (multi-year combined
 *     payoff is the operational scenario the feature was built for).
 *   • Semi-Annual / Quarterly with ≥1 delinquent record → SHOW
 *     (these frequencies naturally produce multiple installments
 *     per year — combined payoff is common).
 *
 * Extracted so the rule is testable in isolation (see
 * `/app/frontend/src/lib/__tests__/combinedPayoffVisibility.test.js`)
 * and reusable by callers other than `JurisdictionManager.jsx`.
 */

const DELINQUENT_DERIVED_STATUSES = new Set(['Delinquent', 'Partially Delinquent']);

/**
 * Decide whether the Combined Delinquent Payoff section should be
 * available for a given jurisdiction + installment set + pay-status
 * deriver.
 *
 * The deriver pattern matches the existing `derivePayStatus` helper in
 * `JurisdictionManager.jsx` so any future status-classification changes
 * land in a single place.
 *
 * @param {object} jurisdiction        — the jurisdiction record (reads
 *                                       `collection_frequency`).
 * @param {Array<object>} installments — list of installment objects.
 * @param {function} derivePayStatus   — `(inst) => 'Delinquent' | 'Partially Delinquent' | ...`.
 * @returns {boolean}                  — true → render the section.
 */
export function isCombinedBreakdownAvailable(jurisdiction, installments, derivePayStatus) {
  if (!installments || installments.length === 0) return false;
  const delinquentCount = installments.reduce((acc, inst) => {
    const s = derivePayStatus(inst);
    return acc + (DELINQUENT_DERIVED_STATUSES.has(s) ? 1 : 0);
  }, 0);
  if (delinquentCount === 0) return false;
  const frequency = ((jurisdiction && jurisdiction.collection_frequency) || 'annually')
    .toString()
    .toLowerCase();
  const isAnnual = frequency === 'annually';
  return !isAnnual || delinquentCount >= 2;
}
